import { eventDayApi } from './EventDayApiClient';
import { loadVenuesFromApi } from './VenueApiService';
import { EDTable } from '../types/EventDay';

// A venue's floor plan can have "table" rooms drawn on it (as opposed to
// zones/areas) — these are the source of truth for event-day tables.
// Creates any event-day table that's missing a match by number, and updates
// section/capacity on existing ones the floor plan disagrees with. Called on
// every Event Day Hub load (not just once when empty) so tables stay
// reconciled as floor plans are edited, not just seeded the first time.
//
// A table is linked to the floor plan (and its venue) it was drawn on, when both ids are uuids. A table the plan disagrees with
// on section or capacity is updated; a table with no link yet gets one; an existing link is never overwritten or cleared by a sync.
// Table numbers are matched across the whole project (not per venue): two venues sharing a number share a table, as before.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuidOrUndefined = (v: unknown): string | undefined => (typeof v === 'string' && UUID.test(v) ? v.toLowerCase() : undefined);

function tableKey(number: number): string {
  return String(number);
}

export async function syncTablesFromFloorPlans(): Promise<{ created: number; updated: number }> {
  const localVenues = await loadVenuesFromApi();
  const fpTableRooms: Array<{ number: number; name?: string; section?: string; capacity?: number; floor_plan_id?: string; venue_id?: string }> = [];
  for (const v of localVenues) {
    if (v.archived) continue; // an archived venue's floor plan is not a live table
    for (const fp of v.floorPlans) {
      for (const room of fp.rooms) {
        if (room.type !== 'table') continue;
        const num = parseInt(room.name);
        if (!isNaN(num) && num > 0) {
          fpTableRooms.push({
            number: num,
            name: room.name === String(num) ? undefined : room.name,
            section: room.section || undefined,
            capacity: room.capacity,
            floor_plan_id: uuidOrUndefined(fp.id),
            venue_id: uuidOrUndefined(v.id),
          });
        }
      }
    }
  }
  const seen = new Set<string>();
  const unique = fpTableRooms.filter(t => {
    const key = tableKey(t.number);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (unique.length === 0) return { created: 0, updated: 0 };

  const { tables: existing } = await eventDayApi.getTables();
  const existingByKey = new Map((existing as EDTable[]).map(t => [tableKey(t.number), t]));

  let created = 0;
  let updated = 0;
  for (const t of unique) {
    const match = existingByKey.get(tableKey(t.number));
    if (!match) {
      await eventDayApi.createTable({ number: t.number, name: t.name, section: t.section, capacity: t.capacity, ...(t.floor_plan_id ? { floor_plan_id: t.floor_plan_id } : {}), ...(t.venue_id ? { venue_id: t.venue_id } : {}) });
      created++;
    } else {
      const needsLink = !match.floor_plan_id && !!t.floor_plan_id;
      if ((t.section && match.section !== t.section) || (t.capacity && match.capacity !== t.capacity) || needsLink) {
        await eventDayApi.updateTable(match.id, {
          number: t.number, section: t.section, capacity: t.capacity,
          ...(needsLink ? { floor_plan_id: t.floor_plan_id, ...(t.venue_id ? { venue_id: t.venue_id } : {}) } : {}),
        });
        updated++;
      }
    }
  }
  return { created, updated };
}
